import { Overlay } from '@angular/cdk/overlay'
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MAP } from '@bases/base.token'
import { DialogCookiesComponent } from '@components/dialogs/dialog-cookies/dialog-cookies.component'
import { DialogNotificationComponent } from '@components/dialogs/dialog-notification/dialog-notification.component'
import { HeaderBarComponent } from '@components/header-bar/header-bar.component'
import { PanelComponent } from '@components/panel/panel.component'
import { ToolsBarComponent } from '@components/tools-bar/tools-bar.component'
import { MAP_OPTIONS, PROJECT_OM4A } from '@core/constants/constant.data'
import { MapService } from '@core/services/map.service'
import { NotificationsService } from '@core/services/notifications.service'
import { SelectionScaleService } from '@core/services/selection-scale.service'
import { environment } from '@env/environment'
import { version } from '@env/version'
import { DrawMap } from 'leaflet'
import 'leaflet-draw' // @TODO: replace with https://github.com/geoman-io/leaflet-geoman ?
import { MatomoTracker } from 'ngx-matomo-client'
import { BehaviorSubject } from 'rxjs'

declare const L: any

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush, //@TODO: check if we put this strategy everywhere (performance !): https://stackoverflow.com/questions/53426412/should-use-changedetectionstrategy-onpush-for-eveything
  imports: [
    // Component
    HeaderBarComponent,
    PanelComponent,
    ToolsBarComponent,
  ],
})
export class MapComponent {
  private readonly _map = inject<BehaviorSubject<DrawMap>>(MAP)
  private readonly _mapService = inject(MapService)
  private readonly _dialog = inject(MatDialog)
  private readonly _tracker = inject(MatomoTracker)
  private readonly _overlay = inject(Overlay)
  private readonly _selectionScaleService = inject(SelectionScaleService)
  private readonly _notificationsService = inject(NotificationsService)

  appName = environment.appName
  PROJECT_OM4A = PROJECT_OM4A
  cookiesVersion = '3'
  version = version
  windowWidth$ = signal<number>(window.innerWidth)

  constructor() {
    // Check if should show the cookie modal
    if (this.cookiesVersion != localStorage.getItem('cookiesAccepted')) {
      const cookieDialogRef = this._dialog.open(DialogCookiesComponent, {
        data: {
          cookiesVersion: this.cookiesVersion,
        },
        disableClose: true,
        scrollStrategy: this._overlay.scrollStrategies.noop(),
      })

      cookieDialogRef.afterClosed().subscribe(() => {
        this._displayPopup()
      })
    } else {
      this._displayPopup()
    }

    //Check if tracking enabled
    const trackingCookies = localStorage.getItem('trackingCookiesAccepted')
    if (trackingCookies && JSON.parse(trackingCookies)) {
      this._tracker.setConsentGiven()
    }
  }

  ngOnInit() {
    this._createMap()
    this._selectionScaleService.addBorderOnMap()
    this._mapService.setupMapService()
  }

  private async _displayPopup() {
    await this._notificationsService.getNotifications()
    let notifications = this._notificationsService.notifications$()
    const readIds: string[] = JSON.parse(localStorage.getItem('notificationNoticeRead') || '[]')

    notifications = notifications.filter((notif) => !readIds.includes(notif.id))

    for (const notif of notifications) {
      await new Promise<void>((resolve) => {
        const ref = this._dialog.open(DialogNotificationComponent, {
          data: notif,
          scrollStrategy: this._overlay.scrollStrategies.noop(),
          disableClose: false,
        })
        ref.afterClosed().subscribe(() => resolve())
      })
    }
  }

  // main method create and display map (main purpose of this component)
  private _createMap() {
    //setup the map from leaflet
    const map = L.map('map', MAP_OPTIONS)

    if (
      (environment.fundingText !== '' || environment.fundingImg !== '') &&
      this.windowWidth$() > 600 // hide funding on very small screen
    ) {
      var eu_logo = L.control({ position: 'bottomright', onAdd: () => {} })

      eu_logo.onAdd = function () {
        var div = L.DomUtil.create('div', 'eu_logo')
        div.style.background = 'rgba(255, 255, 255, 0.8)'
        div.style.padding = '5px'
        div.style.maxWidth = '500px'

        div.style.display = 'flex'
        div.style.alignItems = 'center'

        var logoContainer = document.createElement('div')
        var img = document.createElement('img')
        var textContainer = document.createElement('div')
        var textSpan = document.createElement('span')

        logoContainer.style.display = 'flex'
        logoContainer.style.height = 70 + 'px'
        logoContainer.style.justifyContent = 'center'
        logoContainer.style.marginRight = '10px'

        // image styling
        img.src = environment.fundingImg
        img.style.height = '100%'
        img.style.maxWidth = '100%'

        if (environment.fundingText !== '') {
          // text container styling
          textContainer.style.width = '300px'
          textContainer.style.marginRight = '10px'
          textContainer.style.textAlign = 'justify'
          textContainer.style.lineHeight = '1.2'

          // text span styling
          textSpan.style.fontSize = '10px'
          textSpan.textContent = environment.fundingText
        }

        // append elements to have image first
        logoContainer.appendChild(img)
        div.appendChild(logoContainer)
        textContainer.appendChild(textSpan)
        div.appendChild(textContainer)

        return div
      }
      map.addControl(eu_logo)
    }

    L.Map = L.Map.extend({
      openPopup: function (popup: L.Popup) {
        this.closePopup()
        this._popup = popup
        this._logger.log('MapComponent/popup ', popup)
        return this.addLayer(popup).fire('popupopen', {
          popup: this._popup,
        })
      },
    })

    L.Control = L.Control.extend({
      delete: function (popup: L.Popup) {
        this._popup = popup
        return this.addLayer(popup).fire('popupDelete', {
          popup: this._popup,
        })
      },
    })

    L.control.zoom({ position: 'bottomright' }).addTo(map)

    this._map.next(map)
  }
}
